import React, { useMemo } from "react";
import { Table, Card } from "antd";
import dayjs from "dayjs";

const buildProductTypeSummaryData = (aggEcomDataProductType, stockQuantityData, receivedStockData, dateRange) => {
  if (!aggEcomDataProductType || aggEcomDataProductType.length === 0) return [];

  // Group data by product type and calculate totals
  const productTypeTotals = {};
  
  aggEcomDataProductType.forEach(d => {
    if (d._id.category) { // Exclude totals
      const productType = d._id.category;
      
      if (!productTypeTotals[productType]) {
        productTypeTotals[productType] = {
          productType,
          qtySelectedPeriod: 0,
          totalMTDQty: 0,
          totalValue: 0,
          avgPrice: 0,
          priceCount: 0,
          stockQty: 0,
          receivedQty: 0
        };
      }
      
      productTypeTotals[productType].qtySelectedPeriod += d.totalQty || 0;
      productTypeTotals[productType].totalMTDQty += d.mtdQty || 0;
      productTypeTotals[productType].totalValue += (d.totalValue || 0);
      productTypeTotals[productType].avgPrice += (d.avgPrice || 0);
      productTypeTotals[productType].priceCount += 1;
    }
  });

  // Join with stock data
  if (stockQuantityData && stockQuantityData.length > 0) {
    stockQuantityData.forEach(stockItem => {
      const productType = stockItem.productType;
      if (productTypeTotals[productType]) {
        productTypeTotals[productType].stockQty = stockItem.totalQuantity || 0;
      } else {
        // Add product types that exist in stock but not in transaction data
        productTypeTotals[productType] = {
          productType,
          qtySelectedPeriod: 0,
          totalMTDQty: 0,
          totalValue: 0,
          avgPrice: 0,
          priceCount: 0,
          stockQty: stockItem.totalQuantity || 0,
          receivedQty: 0
        };
      }
    });
  }

  // Join with received stock data
  if (receivedStockData && receivedStockData.length > 0) {
    receivedStockData.forEach(receivedItem => {
      const productType = receivedItem.productType;
      if (productTypeTotals[productType]) {
        productTypeTotals[productType].receivedQty = receivedItem.totalReceivedQuantity || 0;
      } else {
        // Add product types that exist in received stock but not in other data
        productTypeTotals[productType] = {
          productType,
          qtySelectedPeriod: 0,
          totalMTDQty: 0,
          totalValue: 0,
          avgPrice: 0,
          priceCount: 0,
          stockQty: 0,
          receivedQty: receivedItem.totalReceivedQuantity || 0
        };
      }
    });
  }

  // Calculate number of weeks in the selected period
  const calculateWeeksInPeriod = () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      return 1; // Default to 1 week if no date range
    }
    
    const startDate = dayjs(dateRange[0]);
    const endDate = dayjs(dateRange[1]);
    const daysDiff = endDate.diff(startDate, 'day') + 1; // +1 to include both start and end dates
    const weeks = Math.max(1, Math.ceil(daysDiff / 7)); // At least 1 week
    
    return weeks;
  };

  const weeksInPeriod = calculateWeeksInPeriod();

  // Convert to array and calculate final values
  const summaryData = Object.values(productTypeTotals).map(item => ({
    ...item,
    avgPrice: item.priceCount > 0 ? item.avgPrice / item.priceCount : 0,
    pricePoint: item.qtySelectedPeriod > 0 ? item.totalValue / item.qtySelectedPeriod : 0,
    avgQtyPerWeek: weeksInPeriod > 0 ? item.qtySelectedPeriod / weeksInPeriod : 0,
    scw: item.qtySelectedPeriod > 0 && weeksInPeriod > 0 ? 
      item.stockQty / (item.qtySelectedPeriod / weeksInPeriod) : 0
  }));

  // Calculate totals for percentage calculations
  const totalQty = summaryData.reduce((sum, item) => sum + item.qtySelectedPeriod, 0);
  const totalStockQty = summaryData.reduce((sum, item) => sum + item.stockQty, 0);

  // Add percentage calculations and sort by quantity
  return summaryData
    .map(item => ({
      ...item,
      percentageSales: totalQty > 0 ? ((item.qtySelectedPeriod / totalQty) * 100).toFixed(1) : 0,
      percentageStock: totalStockQty > 0 ? ((item.stockQty / totalStockQty) * 100).toFixed(1) : 0
    }))
    .sort((a, b) => b.qtySelectedPeriod - a.qtySelectedPeriod);
};

const EcomProductTypeSummaryTable = ({ aggEcomDataProductType, stockQuantityData, receivedStockData, dateRange }) => {
  const tableData = useMemo(
    () => buildProductTypeSummaryData(aggEcomDataProductType, stockQuantityData, receivedStockData, dateRange),
    [aggEcomDataProductType, stockQuantityData, receivedStockData, dateRange]
  );

  const columns = [
    {
      title: <b>PRODUCT TYPE</b>,
      dataIndex: "productType",
      key: "productType",
      fixed: "left",
      width: 200,
      render: (value) => value || <span style={{ color: "#ccc" }}>Unknown</span>,
    },
    {
      title: <b>QTY SELECTED PERIOD</b>,
      dataIndex: "qtySelectedPeriod",
      key: "qtySelectedPeriod",
      align: "right",
      sorter: (a, b) => a.qtySelectedPeriod - b.qtySelectedPeriod,
      render: (value) => value ? value.toLocaleString("id-ID") : "0",
    },
    {
      title: <b>TOTAL MTD QTY</b>,
      dataIndex: "totalMTDQty",
      key: "totalMTDQty",
      align: "right",
      sorter: (a, b) => a.totalMTDQty - b.totalMTDQty,
      render: (value) => value ? value.toLocaleString("id-ID") : "0",
    },
    {
      title: <b>% SALES</b>,
      dataIndex: "percentageSales",
      key: "percentageSales",
      align: "right",
      sorter: (a, b) => parseFloat(a.percentageSales) - parseFloat(b.percentageSales),
      render: (value) => `${value}%`,
    },
    {
      title: <b>PRICE POINT</b>,
      dataIndex: "pricePoint",
      key: "pricePoint",
      align: "right",
      sorter: (a, b) => a.pricePoint - b.pricePoint,
      render: (value) => value ? `Rp ${Math.round(value).toLocaleString("id-ID")}` : "Rp 0",
    },
    {
      title: <b>STOCK QTY</b>,
      dataIndex: "stockQty",
      key: "stockQty",
      align: "right",
      sorter: (a, b) => a.stockQty - b.stockQty,
      render: (value) => value ? value.toLocaleString("id-ID") : "0",
    },

    {
      title: <b>% STOCK</b>,
      dataIndex: "percentageStock",
      key: "percentageStock",
      align: "right",
      sorter: (a, b) => parseFloat(a.percentageStock) - parseFloat(b.percentageStock),
      render: (value) => `${value}%`,
    },
    {
      title: <b>SCW</b>,
      dataIndex: "scw",
      key: "scw",
      align: "right",
      sorter: (a, b) => a.scw - b.scw,
      render: (value) => value ? value.toFixed(1) : "0.0",
    },
    {
      title: <b>RECEIVED QTY</b>,
      dataIndex: "receivedQty",
      key: "receivedQty",
      align: "right",
      sorter: (a, b) => a.receivedQty - b.receivedQty,
      render: (value) => value ? value.toLocaleString("id-ID") : "0",
    },
  ];

  if (!aggEcomDataProductType || aggEcomDataProductType.length === 0) {
    return (
      <Card
        title={<span style={{ fontWeight: 600 }}>Product Type Summary (Current Stock)</span>}
        style={{
          width: "100%",
          margin: "30px 0",
          boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
          borderRadius: 18,
        }}
      >
        <div style={{ textAlign: "center", padding: "20px", color: "#999" }}>
          No data available
        </div>
      </Card>
    );
  }

  return (
    <Card
      title={<span style={{ fontWeight: 600 }}>Product Type Summary (Current Stock)</span>}
      style={{
        width: "100%",
        margin: "30px 0",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        borderRadius: 18,
      }}
      bodyStyle={{ padding: 0 }}
    >
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        size="middle"
        scroll={{ x: "max-content" }}
        style={{
          borderRadius: 16,
          background: "#fff",
        }}
        rowKey="productType"
      />
    </Card>
  );
};

export default EcomProductTypeSummaryTable;
